import { FC } from "react";
import { Savings } from "@mui/icons-material";
import { pad } from "viem";

import { convertDatabaseAssetToTokenTypeAsset, getEthPrice } from "@framework/exchange";
import { ListAction, ListActionVariant } from "@framework/styled";
import { AddressType, type IStakingRule, StakingRuleStatus } from "@framework/types";
import { useAllowance, useMetamask, useWriteContractWithSimulation } from "@dzambalaorg/react-hooks-eth";
import StakingDepositABI from "@framework/abis/json/Staking/deposit.json";
import { ZeroAddress } from "@framework/constants";
import { useGlobalDialogFormCallbacks } from "@framework/shared";
import { IOnConfirmArgs } from "@dzambalaorg/react-hooks";

import { type IStakingDepositDto } from "./dialog";
import { MarketDialogKeys } from "../../../../../shared";

export interface IStakingDepositComplexButtonProps {
  className?: string;
  disabled?: boolean;
  rule: IStakingRule;
  variant?: ListActionVariant;
}

export const StakingDepositComplexButton: FC<IStakingDepositComplexButtonProps> = props => {
  const { className, disabled, rule, variant } = props;

  const referrer = ZeroAddress;

  const { writeContractAsync } = useWriteContractWithSimulation();

  const { openDialog } = useGlobalDialogFormCallbacks<IStakingRule, IStakingDepositDto>();

  const { execute: metaFnWithAllowance } = useAllowance(async (rule: IStakingRule, values: IStakingDepositDto) => {
    // !!! tokenIds[] must include all deposit tokens !!!
    const params = {
      externalId: BigInt(rule.externalId),
      expiresAt: BigInt(0),
      nonce: pad("0x", { size: 32 }),
      extra: pad("0x", { size: 32 }),
      receiver: ZeroAddress,
      referrer,
    };

    return writeContractAsync({
      address: rule.contract!.address as AddressType,
      abi: StakingDepositABI,
      functionName: "deposit",
      args: [params, values.tokenIds],
      value: getEthPrice(rule.deposit),
    });
  });

  const { execute: metaFn } = useMetamask((rule: IStakingRule, values: IStakingDepositDto) => {
    const price = convertDatabaseAssetToTokenTypeAsset(values.deposit);
    return metaFnWithAllowance(
      {
        contract: rule.contract!.address,
        assets: price,
      },
      rule,
      values,
    );
  });

  const handleDepositConfirm = ({ data }: IOnConfirmArgs<IStakingDepositDto>) => {
    return metaFn(rule, data);
  };

  const handleDeposit = (): void => {
    openDialog(MarketDialogKeys.STAKING_DEPOSIT, rule, {
      initialValues: {
        tokenIds: [0],
        deposit: rule.deposit!.components,
      },
      message: "dialogs.deposit",
      testId: "StakingDepositDialogForm",
      onExternalConfirm: handleDepositConfirm,
    });
  };

  if (rule.stakingRuleStatus !== StakingRuleStatus.ACTIVE) {
    return null;
  }

  return (
    <ListAction
      onClick={handleDeposit}
      icon={Savings}
      message="form.tips.deposit"
      className={className}
      dataTestId="StakingDepositComplexButton"
      disabled={disabled}
      variant={variant}
    />
  );
};
